import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { compareAsc, compareDesc, isValid, parseISO } from 'date-fns'
import { useTheme } from 'styled-components/native'

import { Message, MessageConversation } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import {
  Provider,
  PROVIDER_LYRA_TYPES,
  ProviderInfo,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { aiCoachProviderInfo } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import { useShouldShowAICoach } from '../common/hooks/useShouldShowAICoach'
import {
  getAICoachConversations,
  getConversations,
  getMessengerDrafts,
  getProviders,
  getSelectedConversationId,
} from '../data/lyraTherapy/lyraTherapySelectors'

export interface UseGetSelectedProviderConversationReturn {
  conversations: MessageConversation[]
  providers: Provider[]
  selectedConversation: MessageConversation
  selectedConversationId: string
  selectedProvider?: ProviderInfo
  selectedDraft: Message[]
  isAICoachSelected: boolean
}

type ProviderWithTimestamp = {
  latestMessageDatetime: string | null
} & Provider

export const sortProvidersByLatestMessage = <T extends { latestMessageDatetime: string | null }>(
  providers: readonly T[],
  direction: 'ascending' | 'descending',
): T[] => {
  return [...providers].sort((firstProvider, secondProvider) => {
    const firstTimestampString = firstProvider.latestMessageDatetime
    const secondTimestampString = secondProvider.latestMessageDatetime

    if (!firstTimestampString || !secondTimestampString) {
      return 0
    }

    const firstTimestamp = parseISO(firstTimestampString)
    const secondTimestamp = parseISO(secondTimestampString)
    if (!isValid(firstTimestamp) || !isValid(secondTimestamp)) {
      return 0
    }

    return direction === 'ascending'
      ? compareAsc(firstTimestamp, secondTimestamp)
      : compareDesc(firstTimestamp, secondTimestamp)
  })
}

/**
 * Hook that encapsulates the logic for managing selected provider and conversation state.
 * This hook provides utilities for getting the currently selected provider and conversation.
 */
export const useGetSelectedProviderConversation = (): UseGetSelectedProviderConversationReturn => {
  const { breakpoints } = useTheme() as ThemeType
  const { showAiCoachConversation } = useShouldShowAICoach()
  const selectedMessengerConversationId = useSelector(getSelectedConversationId)
  const conversations = useSelector(getConversations)?.toJS() || []
  const providersList = useSelector(getProviders)?.toJS() || []
  const drafts = useSelector(getMessengerDrafts)?.toJS() || []
  const aiConversations = useSelector(getAICoachConversations)?.toJS() || []

  const aiCoachProviders = aiConversations
    .map((c: MessageConversation) => c.provider_lyra_id)
    .filter((id: string, i: number, arr: string[]) => arr.indexOf(id) === i)
    .map((lyraId: string) => ({
      provider: { ...aiCoachProviderInfo, lyra_id: lyraId },
      appointments: [],
    }))

  const conversationProviderIds = new Set(
    conversations.map((conversation: MessageConversation) => conversation?.provider_lyra_id),
  )

  const providerIdToProvider: { [key: string]: Provider } = {}
  providersList.forEach((provider: Provider) => {
    if (conversationProviderIds.has(provider?.provider?.lyra_id)) {
      providerIdToProvider[provider?.provider?.lyra_id] = provider
    }
  })

  const filteredProviderList: ProviderWithTimestamp[] = conversations
    .map((conversation: MessageConversation) => {
      if (providerIdToProvider[conversation?.provider_lyra_id]) {
        const latestMessageDatetime = conversation?.conversation_last_message_sent_datetime
        return { ...providerIdToProvider[conversation?.provider_lyra_id], latestMessageDatetime }
      } else {
        return null
      }
    })
    .filter((providerWithLatestMessage: ProviderWithTimestamp | null) => providerWithLatestMessage !== null)

  const sortedProviders: Provider[] = useMemo(() => {
    return sortProvidersByLatestMessage(filteredProviderList, breakpoints.isMobileSized ? 'ascending' : 'descending')
  }, [breakpoints.isMobileSized, filteredProviderList])

  const providers = showAiCoachConversation
    ? ([...aiCoachProviders, ...sortedProviders] as Provider[])
    : sortedProviders
  const selectedConversation = selectedMessengerConversationId
    ? conversations.find(
        (conversation: MessageConversation) => conversation.conversation_id === selectedMessengerConversationId,
      )
    : undefined
  const selectedConversationId = selectedConversation?.conversation_id
  const selectedProvider = selectedConversation
    ? providers.find((provider: Provider) => provider.provider.lyra_id === selectedConversation.provider_lyra_id)
        ?.provider
    : undefined
  const selectedDraft = drafts.find((draft: Dict) => draft.conversationId === selectedConversationId)?.content || ''
  const isAICoachSelected = selectedProvider?.lyra_type === PROVIDER_LYRA_TYPES.AI_COACH

  return {
    conversations,
    providers,
    selectedConversation,
    selectedConversationId,
    selectedProvider,
    selectedDraft,
    isAICoachSelected,
  }
}
